import { handleize } from "../utilities/Helpers/helpers";
import { syncCanonicalUrl } from "../utilities/Helpers/CanonicalUrlHelper";
import LaravelErrorResponse from "../utilities/Validation/laravelErrorResponse";
import { handleAriaExpandedClick } from "../utilities/Helpers/Accesibility/Expanded";
import Modal from "../utilities/Helpers/UI/Modal";
import * as Analytics from "../utilities/Analytics/Analytics";
import { createStoreLocatorSearchState } from "../utilities/StoreLocator/storeLocatorSearchState";
import {
    initRecentlyOpenedSwiper,
    initDetailSlider,
    initStoreItemGalleries,
} from "../utilities/StoreLocator/StoreLocatorSwipers";

LUC.jsStoreLocator = {
    alpine: true,
    ...createStoreLocatorSearchState(),
    recentlyOpenedStores: [],
    storeDetail: null,
    errorMessages: null,
    directStoreQueryString: null,
    appointmentFormReadyMaxAttempts: 150,
    appointmentFormPrefillMaxAttempts: 50,
    appointmentFormPrefillStableAttempts: 5,
    init() {
        this.errorMessages = new LaravelErrorResponse({});
        this.updateRecentlyOpenedDynamically();
        this.storeDirectStoreQueryString();
        this.watchHistoryState();
        this.fetchStores().then(() => {
            this.directToStoreOnQuerystringEnter();
        });
    },
    updateRecentlyOpenedDynamically: function () {
        this.$watch("stores", () => {
            this.recentlyOpenedStores = this.stores.filter((store) => store.recently_opened);
            this.$nextTick(() => {
                initRecentlyOpenedSwiper();
                initStoreItemGalleries();
            });
        });
    },
    openDetailPage: function (store) {
        Analytics.trackEvent("store_locator_store_details_opened", {
            store_id: store.id,
            store_title: store.title,
            store_city: store.city,
        });
        this.storeDetail = store;
        history.pushState(JSON.stringify(store), "", `?store=${handleize(store.title)}`);
        syncCanonicalUrl();
        window.scrollTo({ top: 0, behavior: "instant" });
        this.$nextTick(() => initDetailSlider());
    },
    watchHistoryState: function () {
        history.replaceState(undefined, "", document.location.pathname);
        syncCanonicalUrl();
        window.addEventListener("popstate", (evt) => {
            this.storeDetail = evt.state ? JSON.parse(evt.state) : null;
            syncCanonicalUrl();
        });
    },
    openAppointmentModal: function (store) {
        const modal = Modal.getModalByName("contact-modal-container");
        modal.show();
        this.renderAppointmentForm(() => this.ensureAppointmentFormPrefill(store));
    },
    renderAppointmentForm: function (onReady, attempts = 0) {
        const formContent = document.getElementById("contact-modal-form-content");
        if (!formContent) {
            return;
        }

        if (this.isAppointmentFormReady(formContent)) {
            onReady?.();
            return;
        }

        if (
            formContent.dataset.globoFormbuilderInitializing !== "true" &&
            typeof window.Globo?.FormBuilder?.initialize === "function"
        ) {
            formContent.dataset.globoFormbuilderInitializing = "true";
            window.Globo.FormBuilder.initialize();

            if (this.isAppointmentFormReady(formContent)) {
                onReady?.();
                return;
            }
        }

        if (attempts < this.appointmentFormReadyMaxAttempts) {
            setTimeout(() => this.renderAppointmentForm(onReady, attempts + 1), 100);
        }
    },
    isAppointmentFormReady: function (formContent) {
        return !!formContent.querySelector('.globo-form-app [id="45211-select"]');
    },
    getAppointmentStoreDict: function () {
        const storeDict = document.querySelector("[data-sl-dict]")?.dataset.slDict;

        if (!storeDict) {
            return {};
        }

        try {
            return JSON.parse(storeDict.replace(/'/g, '"'));
        } catch (error) {
            console.error("Store Locator appointment store dictionary parse error", error);
            return {};
        }
    },
    getAppointmentStoreData: function (storeInput) {
        const selectedStore = typeof storeInput === "object" && storeInput !== null ? storeInput : null;
        const store_id = selectedStore?.id ?? storeInput;

        if (selectedStore) {
            const storeName = this.getAppointmentStoreName(selectedStore);

            return {
                email: selectedStore.email || selectedStore.store_email || "",
                id: selectedStore.id,
                name: storeName,
                storeNameForAnalytics: storeName,
            };
        }

        const legacyStoreData = this.getAppointmentStoreDict()[store_id];

        if (legacyStoreData) {
            return {
                email: legacyStoreData[1] || "",
                id: store_id,
                name: legacyStoreData[0],
                storeNameForAnalytics: legacyStoreData,
            };
        }

        const store = [this.storeDetail, ...this.stores, ...this.recentlyOpenedStores].find(
            (storeData) => String(storeData?.id) === String(store_id)
        );

        if (!store) {
            return null;
        }

        const storeName = this.getAppointmentStoreName(store);

        return {
            email: store.email || store.store_email || "",
            id: store.id ?? store_id,
            name: storeName,
            storeNameForAnalytics: storeName,
        };
    },
    getAppointmentStoreName: function (store) {
        const city = store?.city || "";
        const title = store?.title || store?.name || "";

        if (!title) {
            return city;
        }

        if (/lucchese/i.test(title)) {
            return title;
        }

        if (/outlet/i.test(title)) {
            return city ? `${title}\u2013${city}` : title;
        }

        return city && city !== title ? `Lucchese at ${title}\u2013${city}` : `Lucchese at ${title}`;
    },
    ensureAppointmentStoreOption: function (storeSelect, storeName) {
        if (!storeName) {
            return;
        }

        const hasOption = Array.from(storeSelect.options).some((option) => option.value === storeName);

        if (!hasOption) {
            storeSelect.add(new Option(storeName, storeName));
        }
    },
    ensureAppointmentFormPrefill: function (storeInput, attempts = 0, hasTracked = false) {
        const storeData = this.getAppointmentStoreData(storeInput);

        if (!storeData) {
            return;
        }

        this.populateAppointmentForm(storeInput, {
            storeData,
            trackAnalytics: !hasTracked,
        });

        const isStable = this.isAppointmentFormPrefilled(storeData);
        const shouldKeepChecking =
            attempts < this.appointmentFormPrefillMaxAttempts &&
            (!isStable || attempts < this.appointmentFormPrefillStableAttempts);

        if (shouldKeepChecking) {
            setTimeout(() => this.ensureAppointmentFormPrefill(storeInput, attempts + 1, true), 100);
        }
    },
    isAppointmentFormPrefilled: function (storeData) {
        const storeSelect = document.getElementById("45211-select");
        return !!storeSelect && !storeSelect.disabled && storeSelect.value === storeData.name;
    },
    populateAppointmentForm: function (storeInput, options = {}) {
        const storeData = options.storeData || this.getAppointmentStoreData(storeInput);

        if (!storeData) {
            return;
        }

        const shouldTrackAnalytics = options.trackAnalytics !== false;
        const appointmentTypeSelect = document.getElementById("45211-select-3");
        const storeSelect = document.getElementById("45211-select");
        const storeEmailInput = document.getElementById("hidden");

        if (appointmentTypeSelect && appointmentTypeSelect.value !== "In Store Appointment") {
            appointmentTypeSelect.value = "In Store Appointment";
            appointmentTypeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }

        if (storeSelect) {
            this.ensureAppointmentStoreOption(storeSelect, storeData.name);
            storeSelect.disabled = false;
            if (storeSelect.value !== storeData.name) {
                storeSelect.value = storeData.name;
                storeSelect.dispatchEvent(new Event("change", { bubbles: true }));
            }
        }

        if (storeEmailInput && storeEmailInput.value !== storeData.email) {
            storeEmailInput.value = storeData.email;
            storeEmailInput.dispatchEvent(new Event("change", { bubbles: true }));
        }

        document.querySelectorAll(".globo-form-control.conditional-field").forEach((field) => {
            field.style.display = "block";
        });

        if (shouldTrackAnalytics) {
            Analytics.trackEvent("store_locator_appointment_modal_opened", {
                store_id: storeData.id,
                store_name: storeData.storeNameForAnalytics,
            });
        }
    },
    unload: function () {},
    findStoreByHandle: function (handle) {
        return this.stores.find((store) => handleize(store.title) === handle);
    },
    directToStoreOnQuerystringEnter: function () {
        if (!this.directStoreQueryString) {
            return;
        }

        const store = this.findStoreByHandle(this.directStoreQueryString);
        if (store) {
            this.openDetailPage(store);
        }
    },
    handleize: function (string) {
        return handleize(string);
    },
    storeDirectStoreQueryString() {
        this.directStoreQueryString = new URLSearchParams(window.location.search).get("store");
    },
    a11yExpandedClick(event) {
        handleAriaExpandedClick(event);
    },
    getStoreImages(store) {
        const isMobile = window.innerWidth < 768;
        const baseImages =
            isMobile && store.images_mobile && store.images_mobile.length > 0
                ? store.images_mobile
                : store.images || [];

        const images = baseImages.filter((img) => img && img.src);

        let banner = null;
        if (isMobile && store.banner_image_mobile && store.banner_image_mobile.src) {
            banner = {
                src: store.banner_image_mobile.src,
                alt: store.banner_image_mobile.alt || store.title,
            };
        } else if (!isMobile && store.banner_image && store.banner_image.src) {
            banner = {
                src: store.banner_image.src,
                alt: store.banner_image.alt || store.title,
            };
        }

        if (!banner) {
            return images;
        }

        const deduped = images.filter((img) => img.src !== banner.src);
        return [banner, ...deduped];
    },
};
